import { useEffect, useCallback } from "react";
import { Configuration, Wall } from "../types/configuration";
import ConfiguratorService from "src/services/configuratorService";
import ConfigurationService from "src/services/configurationService";
import CalculationService from "src/services/calculationService";
import { addHouseWalls, removeHouseWalls } from "src/helpers/walls";
import { validateAndCleanWalls } from "src/helpers/wallValidation";
// @ts-ignore
import isEqual from "lodash/isEqual";
// @ts-ignore
import sortBy from "lodash/sortBy";
import { calculateKapschuurBigHeight } from "src/helpers/configuration";
import {
  isFlatRoofType,
  isRestrictedRoofType,
  isZeroOverhangRoofType,
  isDefaultOverhangRoofType,
  DEFAULT_OVERHANG_FRONT,
} from "src/helpers/roofType";
import prices from "src/prices";

const sortWalls = (wall: Wall) => wall.id;

const useSynchronizeServices = (
  state: Configuration,
  isInitial: boolean,
  configuration: ConfigurationService,
  calculation: CalculationService,
  configurator: ConfiguratorService,
  setWalls: (walls: Configuration["walls"]) => void,
  setIsInitial: (isInitial: boolean) => void,
  setTotalPrice: (totalPrice: string) => void
) => {
  const synchronize = useCallback(() => {
    // Update configurator
    configurator.setState(state);
    const isFlatRoof = isFlatRoofType(state.roofType);
    const isRestrictedRoof = isRestrictedRoofType(state.roofType);
    const isZeroOverhangRoof = isZeroOverhangRoofType(state.roofType);

    const tmpDepth = state.dimensions.depth;
    let updatedWalls = state.walls;
    const currentWalls = configuration.getWalls();

    updatedWalls = configurator.updateCommonStoragesWalls(
      updatedWalls,
      currentWalls
    );

    if (state.roofType === "kapschuur") {
      state.dimensions.height = 3000;
      state.dimensions.overhangBack = 0;
      state.dimensions.overhangFront = 0;
      state.dimensions.overhangLeft = 0;
      state.dimensions.overhangRight = 0;
    }

    if (isZeroOverhangRoof) {
      state.dimensions.overhangBack = 0;
      state.dimensions.overhangFront = 0;
      state.dimensions.overhangLeft = 0;
      state.dimensions.overhangRight = 0;
    }

    if (isFlatRoof) {
      state.dimensions.height = 2200;
    }

    if (isRestrictedRoof) {
      state.roof.epdmGlue = false;
      state.storages.left = false;
      state.storages.right = false;
      state.storages.leftLuifelEnabled = false;
      state.storages.rightLuifelEnabled = false;
      state.optionsMaterial.daktrim_blank = false;
      state.optionsMaterial.daktrim_zwart = false;
      state.optionsMaterial.hemelwaterafvoer_pvc = false;
      state.optionsMaterial.betonband_antraciet = 0;
      state.optionsMaterial.montage = false;
    }

    // Only reset roof.type when roofType actually changes, not on every sync
    const roofTypeChanged =
      !isInitial && configuration.getRoofType() !== state.roofType;
    const optionsMaterialChanged = !isEqual(
      state.optionsMaterial,
      configuration.getOptionsMaterial()
    );

    if (isInitial || configuration.getRoofType() !== state.roofType) {
      state.dimensions.depth = 3000;

      if (isFlatRoof && roofTypeChanged) {
        state.roof.type = "empty";
      }

      // Basisconstructie gets the same default overstek as platdak, also when
      // coming from a roof type that zeroes the overhang (modern, kapschuur).
      const shouldApplyDefaultOverhang =
        state.roofType === "platdak" ||
        (roofTypeChanged && isDefaultOverhangRoofType(state.roofType));

      if (shouldApplyDefaultOverhang) {
        state.dimensions.overhangFront = DEFAULT_OVERHANG_FRONT;
      }

      if (isZeroOverhangRoof) {
        state.dimensions.overhangBack = 0;
        state.dimensions.overhangFront = 0;
        state.dimensions.overhangLeft = 0;
        state.dimensions.overhangRight = 0;
      }

      // if (state.roofType === "kapschuur") {
      //   if (roofTypeChanged) state.roof.type = "empty";
      // }
    }

    // Dimensions & storages
    const currentDimensions = configuration.getDimensions();
    const depthChanged = currentDimensions.depth !== state.dimensions.depth;
    const leftStorageWidthChanged =
      currentDimensions.leftStorageWidth !== state.dimensions.leftStorageWidth;
    const rightStorageWidthChanged =
      currentDimensions.rightStorageWidth !== state.dimensions.rightStorageWidth;
    const shouldValidateWall = (wall: Wall) => {
      // kapschuur allows no doors/windows on h-1 (achter) walls at all. That is
      // a hard rule, not subject to the dimension-change checks below, so
      // storage back walls must be validated even when nothing was resized.
      if (state.roofType === "kapschuur" && wall.id.startsWith("h-1")) {
        return true;
      }

      if (wall.storage === "empty") return true;

      if (depthChanged && wall.id.startsWith("v-")) {
        return true;
      }

      if (
        rightStorageWidthChanged &&
        (wall.storage === "right_front" || wall.storage === "right_back")
      ) {
        return true;
      }

      if (
        leftStorageWidthChanged &&
        (wall.storage === "left_front" || wall.storage === "left_back")
      ) {
        return true;
      }

      return false;
    };

    if (isInitial || !isEqual(currentDimensions, state.dimensions)) {
      configurator.changeSize();
      configurator.changeStorageWidth();
    }

    // Roof type
    if (isInitial || configuration.getRoofType() !== state.roofType) {
      configurator.changeRoofType();
    }

    // Roof
    const currentRoof = configuration.getRoof();

    if (isInitial || !isEqual(currentRoof.type, state.roof.type)) {
      configurator.changeRoofMaterial();
    }

    // Options Material
    if (isInitial || optionsMaterialChanged) {
      const hasRegularTrim =
        state.optionsMaterial.daktrim_blank ||
        state.optionsMaterial.daktrim_zwart;
      const trim = hasRegularTrim ? (state.optionsMaterial.daktrim_blank ? "metalic_ring" : "") : "daktrim_empty";
      configurator.changeRoofCustomization([trim]);
    }

    if (isInitial || roofTypeChanged || optionsMaterialChanged) {
      configurator.changeShorenType(state.optionsMaterial.shorenType);
    }

    // Gutter
    const currentGutter = configuration.getGutter();

    if (isInitial || currentGutter.enabled !== state.gutter.enabled) {
      configurator.toggleGutter();
    }

    if (!isFlatRoofType(configuration.getRoofType()) && isFlatRoof) {
      state.gutter.enabled = false;
      configurator.toggleGutter();
    }

    if (
      state.gutter.enabled &&
      (isInitial || currentGutter.type !== state.gutter.type)
    ) {
      configurator.updateGutterMaterial();
    }

    // Walls and storages
    const prevStorages = configuration.getStorages();

    if (isInitial || prevStorages.left !== state.storages.left) {
      configurator.changeStorageWidth();

      if (state.storages.left) {
        updatedWalls = addHouseWalls(
          "left",
          configuration,
          configurator,
          updatedWalls
        );
      } else {
        updatedWalls = removeHouseWalls("left", updatedWalls);
        configurator.deselectWalls();
      }
    }

    if (isInitial || prevStorages.right !== state.storages.right) {
      configurator.changeStorageWidth();

      if (state.storages.right) {
        updatedWalls = addHouseWalls(
          "right",
          configuration,
          configurator,
          updatedWalls
        );
      } else {
        updatedWalls = removeHouseWalls("right", updatedWalls);
        configurator.deselectWalls();
      }
    }

    // Update walls if they changed

    // update right wall position
    updatedWalls = configurator.updateRightSideWallPositions(updatedWalls);

    // update right storage position
    updatedWalls = configurator.updateRightStoragePositions(updatedWalls);

    updatedWalls = validateAndCleanWalls(
      updatedWalls,
      configurator,
      state.roofType,
      shouldValidateWall
    );

    // update walls with door and window
    updatedWalls = configurator.updateDoorWindowWalls(
      updatedWalls,
      shouldValidateWall
    );

    // update sliding glass door
    updatedWalls = configurator.updateSlidingGlassWalls(updatedWalls);

    updatedWalls = updatedWalls.filter((wall: Wall) => {
      return (
        +wall.id[wall.id.length - 1] <= configurator.getColumnsBalks() / 2 - 1
      );
    });

    if (!isEqual(updatedWalls, state.walls)) {
      state.walls = updatedWalls;
    }

    state.walls = sortBy(state.walls, sortWalls).reverse();

    if (
      isInitial ||
      !isEqual(currentWalls, state.walls) ||
      !isEqual(currentDimensions, state.dimensions)
    ) {
      configurator.updateWalls();
    }

    configurator.updateColumnTypes();

    if (isInitial || configuration.getRoofType() !== state.roofType) {
      state.dimensions.depth = tmpDepth;
      configurator.changeSize();
      configurator.updateWalls();
    }

    // Calculate additional information
    state.params.kapschuurBigHeight = calculateKapschuurBigHeight(state);
    state.columns = configurator.getColumns();

    // Update ConfigurationService
    configuration.setState(state);

    // Update CalculationService
    calculation.setState(state);
    calculation.calculateConfiguration();
    setTotalPrice(calculation.getFormatTotalPrice());

    // //temp prices list
    // console.table(
    //   prices.map((p) => ({
    //     name: p.name,
    //     label: p.label,
    //     unit: p.unit,
    //     price: p.price,
    //     category: p.category,
    //   }))
    // );

    if (isInitial) {
      setIsInitial(false);
      configurator.resetCamera();
    }
  }, [
    calculation,
    configuration,
    configurator,
    isInitial,
    setIsInitial,
    setTotalPrice,
    state,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.CONF && configurator.getColumnsBalks() > 0) {
        clearInterval(interval);
        synchronize();
      }
    }, 700);
  }, [configurator, state, synchronize]);
};

export default useSynchronizeServices;
